import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Layout, message, Table, TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, } from '@ant-design/icons';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { IItemsWithCount, IMobileInfoEntity, KeyValueRecord, transformToFormattedTime } from '@rasayi-workspace/shared';
import { MapAntDesignSearchFilter, MapAntDesignSortOrder, RemoveUndefinedKeyPairs } from '@helpers';
import { BASE_QUERY_OPTIONS, DEFAULT_TABLE_STATE } from '@constants';
import { DeleteItem, EMPTY_INITIAL_ITEMS, GetTableItems } from '@services';
import { BaseLayoutComponent, ColumnsSearchProps, DeleteConfirmationModal } from '@components';
import { ErrorResponse, ITableState } from '@interfaces';
import { MobileInfoModalComponent } from './add-edit';

const { Content } = Layout;

const MobileInfoPageComponent = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [tableState, setTableState] = useState<ITableState>(DEFAULT_TABLE_STATE);
    const [mobileInfosData, setMobileInfosData] = useState<IItemsWithCount<IMobileInfoEntity>>(EMPTY_INITIAL_ITEMS);
    const [isMobileInfoModalOpened, setIsMobileInfoModalOpened] = useState(false);
    const [editingMobileInfoId, setEditingMobileInfoId] = useState('');

    const { refetch, isFetching } = useQuery<IItemsWithCount<IMobileInfoEntity>, AxiosError>({
        ...BASE_QUERY_OPTIONS,
        queryKey: ['mobileInfos'],
        queryFn: () => GetTableItems<IMobileInfoEntity>(
            'mobile_info',
            ['inventory.procurement'],
            [],
            tableState
        ),
        onSuccess: (returnedResult: IItemsWithCount<IMobileInfoEntity>): void => {
setMobileInfosData(returnedResult);},
        onError: () => setMobileInfosData(EMPTY_INITIAL_ITEMS)
    });

    const { mutate: deleteMobileInfo } = useMutation<IMobileInfoEntity, AxiosError>({
        mutationKey: ['deleteMobileInfo'],
        mutationFn: async () =>
            DeleteItem<IMobileInfoEntity>(
                'mobile_info',
                editingMobileInfoId,
            ),
        onSuccess: async (): Promise<void> => {
            messageApi.open({
                type: 'success',
                content: `MobileInfo deleted!`,
            });

            !isFetching && refetch();
            resetModal();
        },
        onError: (error: AxiosError) => {
            messageApi.open({
                type: 'error',
                content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details'
            });
        }
    });

    const handleChange: TableProps<IMobileInfoEntity>['onChange'] = (pagination, filters, sorter) => {
        let sortBy: KeyValueRecord = RemoveUndefinedKeyPairs({
            [(sorter as SorterResult<IMobileInfoEntity>).columnKey as keyof IMobileInfoEntity]: MapAntDesignSortOrder((sorter as SorterResult<IMobileInfoEntity>).order)
        });

        sortBy = Object.keys(sortBy).length ? sortBy : DEFAULT_TABLE_STATE.sortBy;

        setTableState({
            ...tableState,
            page: pagination.current || tableState.page,
            pageSize: pagination.pageSize || tableState.pageSize,
            sortBy: sortBy,
            search: MapAntDesignSearchFilter(RemoveUndefinedKeyPairs(filters))
        });
    };

    const onMobileInfoModalCloseHandler = (newMobileInfo: IMobileInfoEntity) => {
        !isFetching && refetch();
        resetModal();

        messageApi.open(
            !editingMobileInfoId ? {
                type: 'success',
                content: `New mobileInfo '${ newMobileInfo?.id + ' ' + newMobileInfo?.imei_1 }' created!`,
            } : {
                type: 'success',
                content: `MobileInfo '${ newMobileInfo?.id + ' ' + newMobileInfo?.imei_1 }' updated!`,
            }
        );
    };

    const onModalCloseHandler = () => resetModal();

    const resetModal = () => {
        setIsMobileInfoModalOpened(false);
        setEditingMobileInfoId('');
    };

    const resetSearchHandler = (dataIndex: string) => {
        setTableState({
            ...tableState,
            search: tableState.search?.filter((item) => !item.dataKey) || []
        });
    };

    const columns: ColumnsType<IMobileInfoEntity> = [
        {
            title: 'Inventory ID',
            dataIndex: ['inventory', 'internal_id'],
            key: 'inventory.internal_id',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('inventory.internal_id', 'by Inventory ID', resetSearchHandler)
        },
        {
            title: 'Procurement ID',
            dataIndex: ['inventory', 'procurement', 'internal_id'],
            key: 'inventory.procurement.internal_id',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('inventory.procurement.internal_id', 'by Procurement ID', resetSearchHandler)
        },
        {
            title: 'Lock Status',
            dataIndex: 'lock_status',
            key: 'lock_status',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('lock_status', 'by Lock Status', resetSearchHandler)
        },
        {
            title: 'Locking Identifier',
            dataIndex: 'locking_identifier',
            key: 'locking_identifier',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('locking_identifier', 'by Lock Status', resetSearchHandler)
        },
        
        {
            title: 'IMEI 1',
            dataIndex: 'imei_1',
            key: 'imei_1',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('imei_1', 'by IMEI 1', resetSearchHandler)
        },
        {
            title: 'color',
            dataIndex: ['inventory', 'procurement', 'color'],
            key: 'inventory.procurement.color',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('inventory.procurement.color', 'by color', resetSearchHandler)
        },
        {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            ellipsis: true,
            render: (value: string) => transformToFormattedTime(value),
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Last updated at',
            dataIndex: 'updated_at',
            key: 'updated_at',
            ellipsis: true,
            render: (value: string) => transformToFormattedTime(value),
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Actions',
            dataIndex: 'operation',
            key: 'operation',
            render: (_, { id, imei_1 = '', inventory_id = '' }) => (
                <div className='lg:flex'>
                    <Button
                        type='text'
                        shape='circle'
                        icon={ <EditOutlined/> }
                        // disabled={!HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_WRITE])}
                        onClick={
                            () => {
                                setEditingMobileInfoId(id);
                                setIsMobileInfoModalOpened(true);
                            }
                        }
                    />
                    <Button
                        className='ml-2'
                        type='text'
                        shape='circle'
                        icon={ <DeleteOutlined/> }
                        // disabled={!HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_DELETE])}
                        onClick={
                            () => {
                                setEditingMobileInfoId(id);

                                DeleteConfirmationModal({
                                    content: `Are you sure you want to delete mobileInfo '${ imei_1 + ' ' + inventory_id }'?`,
                                    onOk: () => {
                                        deleteMobileInfo();
                                    },
                                    onCancel: () => resetModal()

                                });
                            }
                        }
                    />
                </div>
            ),
        }
    ];

    useEffect(() => {
        !isFetching && refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState]);

    return (
        <>
            { contextHolder }
            {
                isMobileInfoModalOpened &&
                <MobileInfoModalComponent
                    editMode={ !!editingMobileInfoId }
                    id={ editingMobileInfoId }
                    onClose={ onModalCloseHandler }
                    onSubmit={ onMobileInfoModalCloseHandler }
                />
            }
            <BaseLayoutComponent children={
                <Content className='m-5 bg-white border rounded-lg'>
                    <Card
                        title={ 'Mobile info' }
                    >
                        <div className='border-2 border-black rounded-lg'>
                            <Table
                                columns={ columns }
                                dataSource={ mobileInfosData.items }
                                onChange={ handleChange }
                                pagination={
                                    {
                                        total: mobileInfosData.count,
                                        pageSize: mobileInfosData.pageSize,
                                        current: mobileInfosData.page,
                                    }
                                }
                                loading={ isFetching }
                                rowKey={ 'id' }
                                size='small'
                                scroll={ { x: true } }
                            />
                        </div>
                    </Card>
                </Content>
            }
            />
        </>
    );
};

export default MobileInfoPageComponent;